// SmartBanner dimensions and calculations
export const SMARTBANNER_HEIGHT = 80; // 4.25rem in pixels
export const SMARTBANNER_SAFE_AREA_PADDING = 12; // Minimum padding in pixels

// Check if device is iOS
const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent);
};

// Check if device is mobile (iOS or Android)
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);

  return isIOS || isAndroid;
};

// Role-based configuration for SmartBanner
export interface SmartBannerConfig {
  role?: 'customer' | 'cleaner' | null;
  isVisible?: boolean;
  hasNavbar?: boolean;
}

// Utility function to calculate padding with safe area
export const getSmartBannerPadding = (config: SmartBannerConfig): string => {
  const { isVisible = false, hasNavbar = true } = config;

  // If current route doesn't have navbar, no padding needed
  if (!hasNavbar) {
    // Still need iOS safe area for notch even on auth pages
    if (isIOSDevice()) {
      return `max(${SMARTBANNER_SAFE_AREA_PADDING}px, env(safe-area-inset-top))`;
    }
    return '0px';
  }

  // SmartBanner only shows on mobile, so on desktop we only need base padding
  if (isVisible && isMobileDevice()) {
    // On iOS, include safe area inset for notch
    if (isIOSDevice()) {
      return `calc(${SMARTBANNER_HEIGHT}px + max(${SMARTBANNER_SAFE_AREA_PADDING}px, env(safe-area-inset-top)))`;
    }
    // On Android, only add SmartBanner height
    return `${SMARTBANNER_HEIGHT}px`;
  }

  // When SmartBanner is not visible
  if (isIOSDevice()) {
    // On iOS, still need safe area for notch
    return `max(${SMARTBANNER_SAFE_AREA_PADDING}px, env(safe-area-inset-top))`;
  }
  // On Android/Desktop, no padding needed
  return '0px';
};

// CSS class names for transitions
export const SMARTBANNER_TRANSITION =
  'padding-top 500ms cubic-bezier(0.4, 0, 0.2, 1)';
